import * as Sentry from '@sentry/browser';

import {
  type AssetsShareLoginSchema,
  type AssetsShareTokenSchema,
  httpGetSharesAuthToken,
  httpPostSharesAuthLogin,
  httpPostSharesByShareIdMagicLinkRequest,
  httpPostSharesByShareIdMagicLinkValidate,
  httpPostSharesMagicLinkEnabled,
} from '@iconik-io/iconik-api/src/services/assets';

import {
  isBadRequestError,
  isForbiddenError,
  isInvalidPasswordError,
  isUnauthorizedError,
} from '../../utils/handle_messages';
import { shareTokenManager } from '../../utils/share/share_token_manager';
import {
  keepCredentialsOnUnauthorized,
  skipRedirectOnLogin,
} from '../middlewares/http/actions';
import { getShareLoginCredentials } from '../middlewares/storage/credentials';
import { createAppThunk } from '../types';

/** How a host installs a share token into its own store. */
export type ApplyShareToken = (
  token: AssetsShareTokenSchema,
) => (...args: never[]) => unknown;

export type ShareLoginResult = {
  passwordRequired?: boolean;
  magicLinkRequired?: boolean;
};

export type ShareMagicLinkLoginParams = {
  shareId: string;
  hash: string;
  email: string;
  password?: string;
};

export type ShareMagicLinkLoginResult = {
  passwordRequired?: boolean;
  validationFailed?: boolean;
  token?: AssetsShareTokenSchema;
};

export type StoredShareTokenResult =
  | { status: 'valid'; token: AssetsShareTokenSchema }
  /** The server refused the token, so the record is worth discarding. */
  | { status: 'rejected' }
  /** No record, or the check could not be made: keep whatever is stored. */
  | { status: 'unverified' };

/**
 * Checks a stored magic-link token against the server, so a repeat visit does not
 * consume the single-use hash again.
 *
 * Reports a refusal separately from a failure to ask. The hash is spent by the time
 * a handover token exists, so discarding the record on a transient error would leave
 * the viewer with no credential at all and no way back but a fresh email.
 */
export const getValidStoredShareToken = createAppThunk(
  (shareId: string) =>
    (dispatch): Promise<StoredShareTokenResult> => {
      const record = shareTokenManager.getTokenRecord(shareId);
      if (!record) {
        return Promise.resolve({ status: 'unverified' });
      }
      return dispatch(
        // A rejection here is of the share token in the headers, not of the
        // viewer's own session, which the default 401 handling would clear.
        keepCredentialsOnUnauthorized(
          skipRedirectOnLogin(
            httpGetSharesAuthToken({
              'App-ID': record.token.app_id,
              'Share-Auth-Token': record.token.token,
            }),
          ),
        ),
      )
        .then(
          (): StoredShareTokenResult => ({
            status: 'valid',
            token: record.token,
          }),
        )
        .catch((error: unknown): StoredShareTokenResult => {
          if (isUnauthorizedError(error) || isForbiddenError(error)) {
            return { status: 'rejected' };
          }
          return { status: 'unverified' };
        });
    },
);

export function makeShareLoginThunk(applyShareToken: ApplyShareToken) {
  return createAppThunk(
    (data: AssetsShareLoginSchema) =>
      async (dispatch): Promise<ShareLoginResult> => {
        try {
          const response = await dispatch(
            httpPostSharesAuthLogin({
              ...data,
              ...getShareLoginCredentials(),
            }),
          );
          await dispatch(applyShareToken(response.body));
          return {};
        } catch (error: unknown) {
          if (isInvalidPasswordError(error)) {
            return { passwordRequired: true };
          }
          // Only a rejected login can be answered with a magic link. The probe
          // reports on the domain rather than on this error, so running it after
          // a 5xx or a network failure would name the wrong reason and bury the
          // real one.
          if (!isBadRequestError(error) || !data.hash) {
            throw error;
          }
          // Anonymous logins are refused when the domain requires an identified
          // viewer. The probe tells us whether a magic link can supply that
          // identity, in which case the caller collects an email address.
          const magicLinkRequired = await dispatch(
            httpPostSharesMagicLinkEnabled({ hash: data.hash }),
          )
            .then(() => true)
            .catch(() => false);
          if (magicLinkRequired) {
            return { magicLinkRequired: true };
          }
          throw error;
        }
      },
  );
}

/**
 * Returns the token without installing it: the landing hands it to the share page,
 * which installs the session itself. Resolves rather than throws so the caller can
 * offer a password prompt or a resend.
 */
export const shareMagicLinkLogin = createAppThunk(
  ({ shareId, hash, email, password }: ShareMagicLinkLoginParams) =>
    async (dispatch): Promise<ShareMagicLinkLoginResult> => {
      try {
        const response = await dispatch(
          httpPostSharesByShareIdMagicLinkValidate(shareId, {
            hash,
            email,
            password,
            ...getShareLoginCredentials(),
          }),
        );
        return { token: response.body };
      } catch (error: unknown) {
        if (isInvalidPasswordError(error)) {
          return { passwordRequired: true };
        }
        // 400 means the single-use hash is expired or already consumed; the
        // caller offers a resend rather than treating it as a hard failure.
        if (isBadRequestError(error)) {
          return { validationFailed: true };
        }
        // The landing page dead-ends here, so report it rather than losing it.
        Sentry.captureException(error);
        return {};
      }
    },
);

export const requestShareMagicLink = createAppThunk(
  (shareId: string, email: string) =>
    (dispatch): Promise<void> =>
      dispatch(
        httpPostSharesByShareIdMagicLinkRequest(shareId, { email }),
      ).then(() => undefined),
);
